import { Component } from '@theme/component';
import { debounce, onDocumentReady } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Only add event listener if overflow menu exists
    if (this.overflowMenu) {
      this.overflowMenu.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
        signal: this.#abortController.signal,
      });
    }

    onDocumentReady(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(
      () => {
        item.removeAttribute('data-animating');
      },
      Math.max(0, this.animationDelay - 150)
    ); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

const NAV_SELECTOR = '.header-menu__inner .menu-list';

/** @type {HTMLElement | null} */
let lastOpenedToggle = null;

/**
 * @param {Element} li
 */
function closeMenu(li) {
  li.removeAttribute('data-open');
  li.querySelector('a[data-nav-toggle]')?.setAttribute('aria-expanded', 'false');
}

function closeAllMenus({ returnFocus = false } = {}) {
  // Return focus to the top-level toggle: a nested toggle (e.g. a Shop category) sits inside the
  // panel that is closing, so focusing it would leave focus on a hidden element.
  const topToggle = document.querySelector(`${NAV_SELECTOR} .menu-primary > li[data-open] > a[data-nav-toggle]`);
  const focusTarget = topToggle ?? lastOpenedToggle;
  document.querySelectorAll(`${NAV_SELECTOR} li[data-open]`).forEach(closeMenu);
  if (returnFocus && focusTarget?.isConnected) {
    focusTarget.focus();
  }
  lastOpenedToggle = null;
}

/**
 * Close every open menu except the given item's own ancestor chain, so opening a nested
 * submenu (e.g. a Shop category) keeps its parent panel open.
 * @param {Element} keepLi
 */
function closeMenusExcept(keepLi) {
  document.querySelectorAll(`${NAV_SELECTOR} li[data-open]`).forEach((li) => {
    if (li.contains(keepLi)) return;
    closeMenu(li);
  });
}

/**
 * @param {HTMLElement} li
 * @param {HTMLElement} toggle
 */
function openMenu(li, toggle) {
  lastOpenedToggle = toggle;
  li.setAttribute('data-open', '');
  toggle.setAttribute('aria-expanded', 'true');
}

/**
 * Toggle the submenu owned by a nav toggle, preserving any open ancestor menus.
 * @param {HTMLElement} toggle
 */
function toggleMenu(toggle) {
  const li = toggle.closest('li');
  if (!(li instanceof HTMLElement)) return;
  const isOpen = li.hasAttribute('data-open');
  closeMenusExcept(li);
  if (isOpen) {
    closeMenu(li);
    lastOpenedToggle = null;
  } else {
    openMenu(li, toggle);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const toggle = target.closest(`${NAV_SELECTOR} a[data-nav-toggle]`);

  if (toggle instanceof HTMLElement) {
    event.preventDefault();
    toggleMenu(toggle);
    return;
  }

  if (!target.closest(NAV_SELECTOR)) {
    closeAllMenus();
  }
});

// Close any open (click) menu when keyboard focus leaves the nav entirely. Without this, a
// user tabbing past an open panel would leave it open (Escape and click-outside also close).
document.addEventListener('focusout', (event) => {
  const next = event.relatedTarget;
  if (next instanceof Element && next.closest(NAV_SELECTOR)) return;
  closeAllMenus();
});

document.addEventListener('theme:section:morphed', () => {
  closeAllMenus();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') {
    closeAllMenus({ returnFocus: true });
    return;
  }

  if (event.key === ' ') {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest(`${NAV_SELECTOR} a[data-nav-toggle]`);
    if (toggle instanceof HTMLElement) {
      event.preventDefault();
      toggleMenu(toggle);
    }
  }
});
